import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Tag } from 'antd';
import {
  HomeOutlined,
  VideoCameraOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CrownOutlined,
  MailOutlined,
  TagsOutlined,
  ScheduleOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout, setUser } from '../store/slices/authSlice';
import './AppLayout.css';

const { Header, Sider, Content } = Layout;

const AppLayout = ({ children, activeMenu }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  // Initialize user from localStorage on mount
  useEffect(() => {
    if (!user && isAuthenticated) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const token = localStorage.getItem('token');
          dispatch(setUser({ user: userData, token, isAuthenticated: true }));
        } catch (error) {
          console.error('Failed to parse stored user:', error);
        }
      }
    }
  }, [user, isAuthenticated, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Determine user role
  const userRole = user?.role || (user?.is_admin ? 'admin' : 'user');
  const isAdmin = userRole === 'admin' || user?.is_admin;
  const isVerifier = userRole === 'verifier';

  // Build menu items based on user role
  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: 'Leaderboard',
    },
    {
      key: '/my-videos',
      icon: <VideoCameraOutlined />,
      label: 'Video Saya',
    },
    {
      key: '/certifications',
      icon: <SafetyCertificateOutlined />,
      label: 'Sertifikasi AI',
    },
  ];

  // Add admin section for admins
  if (isAdmin) {
    menuItems.push({
      type: 'divider',
    });
    menuItems.push({
      key: 'admin-section',
      type: 'group',
      label: collapsed ? '' : 'ADMIN',
      children: [
        {
          key: '/verification',
          icon: <CheckCircleOutlined />,
          label: 'Verification',
        },
        {
          key: '/admin/hashtags',
          icon: <TagsOutlined />,
          label: 'Hashtags',
        },
        {
          key: '/admin/scheduler',
          icon: <ScheduleOutlined />,
          label: 'Scheduler',
        },
        {
          key: '/admin/users',
          icon: <TeamOutlined />,
          label: 'Users',
        },
        {
          key: '/admin/config',
          icon: <SettingOutlined />,
          label: 'Configuration',
        },
      ],
    });
  }

  // Add verificator panel for verifiers (non-admin)
  if (isVerifier && !isAdmin) {
    menuItems.push({
      type: 'divider',
    });
    menuItems.push({
      key: '/verificator',
      icon: <CheckCircleOutlined />,
      label: 'Verificator Panel',
    });
  }

  const userMenu = {
    items: [
      {
        key: 'profile-header',
        label: (
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px', color: '#000' }}>
              {user?.name || 'User'}
            </div>
            <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <MailOutlined style={{ fontSize: '11px' }} />
              {user?.email || 'No email'}
            </div>
            {isAdmin && (
              <Tag color="blue" icon={<CrownOutlined />} style={{ marginTop: '8px', fontSize: '11px' }}>
                Administrator
              </Tag>
            )}
            {isVerifier && !isAdmin && (
              <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginTop: '8px', fontSize: '11px' }}>
                Verifier
              </Tag>
            )}
          </div>
        ),
        disabled: true,
      },
      {
        type: 'divider',
      },
      {
        key: '/my-videos',
        icon: <VideoCameraOutlined />,
        label: 'Video Saya',
        onClick: () => navigate('/my-videos'),
      },
      {
        key: '/certifications',
        icon: <SafetyCertificateOutlined />,
        label: 'Sertifikasi Saya',
        onClick: () => navigate('/certifications'),
      },
      isAdmin && {
        key: '/admin',
        icon: <SettingOutlined />,
        label: 'Admin Panel',
        onClick: () => navigate('/admin'),
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        onClick: handleLogout,
        danger: true,
      },
    ].filter(Boolean),
  };

  const handleMenuClick = ({ key }) => {
    if (key && !key.startsWith('admin-section')) {
      navigate(key);
    }
  };

  // Determine selected key
  const getSelectedKey = () => {
    if (activeMenu) return activeMenu;
    const path = location.pathname;
    if (path.startsWith('/admin/hashtags')) return '/admin/hashtags';
    if (path.startsWith('/admin/scheduler')) return '/admin/scheduler';
    if (path.startsWith('/admin/users')) return '/admin/users';
    if (path.startsWith('/admin/config')) return '/admin/config';
    if (path.startsWith('/verification') || path.startsWith('/admin')) return '/verification';
    if (path.startsWith('/verificator')) return '/verificator';
    if (path.startsWith('/certifications')) return '/certifications';
    if (path.startsWith('/my-videos')) return '/my-videos';
    return path;
  };

  return (
    <Layout className="app-layout-sidebar" style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #14B8A6 0%, #14B8A6 100%)',
        }}
      >
        <div
          className="logo"
          onClick={() => navigate('/')}
          style={{
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: collapsed ? '16px' : '18px',
            fontWeight: 'bold',
            padding: '0 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            cursor: 'pointer',
            transition: 'background 0.3s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          {collapsed ? 'GA' : 'GAMIFIKASI'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          defaultOpenKeys={isAdmin ? ['admin-section'] : []}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            background: 'transparent',
            border: 'none',
          }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'all 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />

          <Space size="large">
            {isAuthenticated ? (
              <Dropdown menu={userMenu} placement="bottomRight">
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar
                    src={user?.picture}
                    icon={<UserOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #14B8A6 0%, #14B8A6 100%)',
                    }}
                  />
                  <span>
                    {user?.name || user?.email}
                  </span>
                </Space>
              </Dropdown>
            ) : (
              <Button type="primary" onClick={() => navigate('/login')}>
                Login
              </Button>
            )}
          </Space>
        </Header>
        <Content
          style={{
            margin: '24px',
            padding: 24,
            minHeight: 280,
            background: '#f0f2f5',
            borderRadius: 8,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
